/*
 * Scentt wishlist \u2013 hearts, header count and the wishlist page tools.
 *
 * Hearts: any [data-scentt-wish="<handle>"] button (cards, product page). ONE delegated click listener
 *   toggles the handle in Scentt.list('scentt:wishlist') and keeps aria-pressed / .is-active /
 *   [data-scentt-label] in sync on every heart; [data-scentt-wish-count] shows the number saved.
 * <scentt-wishlist data-copied data-subject data-body> (sections/scentt-wishlist.liquid):
 *   restores a list shared with ?wl=handle-a,handle-b (validated, max 100), and offers
 *   "copy my link" / "email it to me". The product grid itself is <scentt-rail-cards data-source="wishlist">.
 */
(() => {
  const start = () => {
    const S = window.Scentt && Object.isFrozen(window.Scentt) && typeof window.Scentt.html === 'function' ? window.Scentt : null;
    if (!S || customElements.get('scentt-wishlist')) return;

    const MAX = 100;
    const wishlist = S.list('scentt:wishlist', MAX);
    const HANDLE = /^[a-z0-9-]+$/;

    const sync = () => {
      const items = wishlist.all();
      document.querySelectorAll('[data-scentt-wish]').forEach((b) => {
        const on = items.includes(b.getAttribute('data-scentt-wish'));
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        const label = b.querySelector('[data-scentt-label]');
        if (label) {
          const text = on ? label.getAttribute('data-on') : label.getAttribute('data-off');
          if (text) label.textContent = text;
        }
      });
      document.querySelectorAll('[data-scentt-wish-count]').forEach((c) => {
        c.textContent = String(items.length);
        c.hidden = items.length === 0;
      });
      document.querySelectorAll('.header__icon--wishlist').forEach((a) => a.classList.toggle('is-active', items.length > 0));
    };

    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('[data-scentt-wish]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const h = btn.getAttribute('data-scentt-wish');
      if (!S.isHandle(h)) return;
      const adding = !wishlist.has(h);
      wishlist.toggle(h);
      btn.classList.remove('scentt-pop');
      if (adding && !S.reducedMotion) {
        void btn.offsetWidth; /* restart the animation */
        btn.classList.add('scentt-pop');
      }
    }, true);
    wishlist.onChange(sync);
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; sync(); });
    }).observe(document.body || document.documentElement, { childList: true, subtree: true });
    sync();

    /** Handles from a shared ?wl= link: lower-case letters, digits and dashes only, max 100. */
    const parseShared = (raw) => {
      if (typeof raw !== 'string' || !raw) return [];
      return raw.slice(0, 26000).split(',').map((h) => h.trim())
        .filter((h) => h.length <= 255 && HANDLE.test(h))
        .filter((h, i, a) => a.indexOf(h) === i)
        .slice(0, MAX);
    };

    class ScenttWishlist extends HTMLElement {
      connectedCallback() {
        this.save = this.querySelector('[data-scentt-wl-save]');
        this.restored = this.querySelector('[data-scentt-wl-restored]');
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);
        this.restore();
        this.off = wishlist.onChange(() => this.update());
        this.update();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        if (this.off) this.off();
        clearTimeout(this.timer);
      }

      /* A shared ?wl= link only shows a preview; nothing is saved until the shopper chooses "Add". */
      restore() {
        let params;
        try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
        if (!params.has('wl')) return;
        this.shared = parseShared(params.get('wl'));
        params.delete('wl');
        const query = params.toString();
        try {
          window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
        } catch (e) { /* history blocked (sandboxed iframe) */ }
        const panel = this.querySelector('[data-scentt-wl-shared]');
        const rail = panel && panel.querySelector('scentt-rail-cards');
        if (!this.shared.length || !panel) return;
        panel.hidden = false;
        if (rail) {
          rail.dataset.handles = this.shared.join(',');
          /* the cards component may load after this script: wait until it is defined */
          customElements.whenDefined('scentt-rail-cards').then(() => { if (typeof rail.load === 'function') rail.load(); });
        }
      }

      acceptShared() {
        const current = wishlist.all();
        const room = MAX - current.length;
        /* only what the shopper was shown (deleted/unpublished products never render) */
        const shown = Array.from(this.querySelectorAll('[data-scentt-wl-shared] [data-handle]'))
          .map((el) => el.getAttribute('data-handle')).filter((h) => (this.shared || []).includes(h));
        const pick = shown.length ? shown : (this.shared || []).slice(0, 48);
        pick.filter((h) => !current.includes(h)).slice(0, Math.max(room, 0))
          .forEach((h) => wishlist.add(h, { front: false }));
        this.closeShared();
        if (this.restored) this.restored.hidden = false;
      }

      closeShared() {
        this.shared = [];
        const panel = this.querySelector('[data-scentt-wl-shared]');
        if (panel) panel.hidden = true;
      }

      link() {
        const items = wishlist.all().filter((h) => HANDLE.test(h));
        return `${window.location.origin}${window.location.pathname}?wl=${items.join(',')}`;
      }

      update() {
        const count = wishlist.all().length;
        if (this.save) this.save.hidden = count === 0;
        const mail = this.querySelector('[data-scentt-wl-email]');
        if (mail) {
          const link = this.link();
          const body = String(this.dataset.body || '[link]');
          const text = body.includes('[link]') ? body.replace('[link]', () => link) : `${body} ${link}`;
          mail.setAttribute('href', `mailto:?subject=${encodeURIComponent(this.dataset.subject || '')}&body=${encodeURIComponent(text)}`);
        }
      }

      showLink(link) {
        const wrap = this.querySelector('[data-scentt-wl-link-wrap]');
        const input = this.querySelector('[data-scentt-wl-link]');
        if (!wrap || !input) return;
        input.value = link;
        wrap.hidden = false;
        input.focus();
        input.select();
      }

      copied(btn) {
        const label = btn.querySelector('[data-scentt-wl-copy-label]');
        if (!label) return;
        if (!this.original) this.original = label.textContent;
        label.textContent = this.dataset.copied || this.original;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => { label.textContent = this.original; }, 2200);
      }

      onClick(e) {
        if (e.target.closest('[data-scentt-wl-accept]')) { this.acceptShared(); return; }
        if (e.target.closest('[data-scentt-wl-dismiss]')) { this.closeShared(); return; }
        const btn = e.target.closest('[data-scentt-wl-copy]');
        if (!btn) return;
        const link = this.link();
        const phone = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
        if (phone && navigator.share) {
          navigator.share({ title: document.title, url: link }).catch(() => { /* cancelled */ });
          return;
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(link).then(() => this.copied(btn), () => this.showLink(link));
        } else {
          this.showLink(link);
        }
      }
    }
    ScenttWishlist.parseShared = parseShared;
    customElements.define('scentt-wishlist', ScenttWishlist);
  };

  if (window.Scentt) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
